import axios from "axios";

const axiosInstance = axios.create();

/**
 * Call this once (e.g. in App.jsx or AuthProvider) after auth is ready.
 *
 * @param {React.MutableRefObject} authDataRef  - ref always pointing to latest authData
 * @param {Function}               setAuthData  - state setter from AuthContext
 * @param {string}                 backUrl      - base API URL
 * @param {Function}               navigate     - react-router navigate fn
 * @returns {Function}  cleanup fn — call it on unmount to eject interceptors
 */
export const setupInterceptors = (authDataRef, setAuthData, backUrl, navigate) => {
  /* ── 1. REQUEST: auto-attach Bearer token ── */
  const reqId = axiosInstance.interceptors.request.use(
    (config) => {
      const token = authDataRef.current?.access;
      if (token && !config.headers["Authorization"]) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  /* ── 2. RESPONSE: silent token refresh on 401 ── */
  let isRefreshing = false;
  let failedQueue = [];

  const processQueue = (error, token = null) => {
    failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
    failedQueue = [];
  };

  const resId = axiosInstance.interceptors.response.use(
    (response) => response,

    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          // Queue requests that arrive while a refresh is already in progress
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers["Authorization"] = `Bearer ${token}`;
              return axiosInstance(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const refreshToken = authDataRef.current?.refresh;

          if (!refreshToken) throw new Error("No refresh token available");

          // Use plain axios (not axiosInstance) to avoid interceptor loops
          const { data } = await axios.post(`${backUrl}/api/token/refresh/`, {
            refresh: refreshToken,
          });

          const newAccessToken = data.access;

          // Update context + localStorage atomically
          setAuthData((prev) => ({ ...prev, access: newAccessToken }));

          const stored = localStorage.getItem("authData");
          if (stored) {
            const parsed = JSON.parse(stored);
            parsed.access = newAccessToken;
            localStorage.setItem("authData", JSON.stringify(parsed));
          }

          // Also update authDataRef immediately so in-flight requests use it
          authDataRef.current = { ...authDataRef.current, access: newAccessToken };

          originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
          processQueue(null, newAccessToken);

          return axiosInstance(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);

          setAuthData(null);
          localStorage.removeItem("authData");
          navigate("/logout");

          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );

  // Return cleanup function
  return () => {
    axiosInstance.interceptors.request.eject(reqId);
    axiosInstance.interceptors.response.eject(resId);
  };
};

export default axiosInstance;